import type { FramePresentationEvidence, PresentationRecoveryDecision } from '../streamRecovery/contracts';
import type { PresentationRecoveryPolicy } from '../streamRecovery/interfaces';
import { FRAME_EVIDENCE_STALE_MS, VISIBLE_STALL_KEYFRAME_MS, VISIBLE_STALL_REFRESH_MS } from '../streamRecovery/policy';

type Observation = { evidence: FramePresentationEvidence; peerConnected: boolean; nowMs: number };

/** Corroborated presentation recovery. Decode progress alone does not prove display progress. */
export class VisiblePresentationRecovery implements PresentationRecoveryPolicy {
  private static nextInstanceId = 0;
  private readonly instanceId = ++VisiblePresentationRecovery.nextInstanceId;
  private previous?: FramePresentationEvidence;
  private lastProgressMs?: number;
  private visibleSinceMs?: number;
  private episodeCount = 0;
  private episodeId?: string;
  private keyframeRequested = false;
  private refreshRequested = false;
  private resumeBaseline = false;
  private decoder?: { sequence: number; frames: number; observedAtMs: number };
  private decoderFlat = false;

  observe({ evidence, peerConnected, nowMs }: Observation): PresentationRecoveryDecision {
    if (evidence.visibility === 'hidden' || evidence.playback === 'paused' || evidence.playback === 'ended') {
      this.reset();
      this.resumeBaseline = true;
      return this.result('suspended');
    }
    if (evidence.visibility !== 'visible' || evidence.playback !== 'playing' || !Number.isFinite(nowMs)) {
      this.reset();
      this.resumeBaseline = true;
      return this.result('unknown');
    }
    if (this.visibleSinceMs === undefined) this.visibleSinceMs = nowMs;
    const decodeFlat = this.observeDecoder(evidence, nowMs);
    const previous = this.previous;
    this.previous = evidence;
    if (this.counterReset(evidence, previous)) {
      this.lastProgressMs = nowMs;
      this.clearEpisode();
      return this.result('unknown');
    }
    const presented = this.playbackFrames(evidence);
    const previousPresented = previous ? this.playbackFrames(previous) : undefined;
    const progress = previous && (evidence.progressSequence > previous.progressSequence
      || this.advanced(evidence.presentedFrames, previous.presentedFrames)
      || this.advanced(presented, previousPresented));
    if (progress) {
      this.lastProgressMs = nowMs;
      this.clearEpisode();
      return this.result('healthy');
    }
    const firstFrame = evidence.progressSequence > 0 || (evidence.presentedFrames ?? 0) > 0 || (presented ?? 0) > 0;
    if (!firstFrame) return this.result('unknown');
    const age = evidence.lastPresentationAgeMs ?? evidence.callbackAgeMs;
    if (this.lastProgressMs === undefined && age !== undefined && Number.isFinite(age) && age >= 0) {
      this.lastProgressMs = this.resumeBaseline ? nowMs : nowMs - age;
      this.resumeBaseline = false;
    }
    if (this.lastProgressMs === undefined) return this.result('unknown');
    const elapsed = Math.max(0, nowMs - this.lastProgressMs);
    if (elapsed < VISIBLE_STALL_KEYFRAME_MS) return this.result('healthy');
    const playbackFlat = previousPresented !== undefined && presented === previousPresented;
    if (!playbackFlat && !decodeFlat) return this.result('unknown');
    if (!this.episodeId) this.episodeId = `presentation-${this.instanceId}-${++this.episodeCount}`;
    const action = peerConnected ? this.nextAction(elapsed) : 'none';
    return { state: 'stalled', episodeId: this.episodeId, stalledForMs: elapsed, action };
  }

  reset(): void {
    this.previous = undefined;
    this.lastProgressMs = undefined;
    this.visibleSinceMs = undefined;
    this.decoder = undefined;
    this.decoderFlat = false;
    this.clearEpisode();
  }

  /** Watchdog rereads preserve established corroboration but cannot establish it. */
  private observeDecoder(evidence: FramePresentationEvidence, nowMs: number): boolean {
    const observation = evidence.decodeObservation;
    const frames = evidence.decodedFrames;
    const previous = this.decoder;
    if (!observation || frames === undefined || !Number.isSafeInteger(frames) || frames < 0 ||
        !Number.isSafeInteger(observation.sequence) || observation.sequence < 0 ||
        !Number.isFinite(observation.ageMs) || observation.ageMs < 0 || observation.ageMs >= FRAME_EVIDENCE_STALE_MS) {
      this.invalidateDecoder(nowMs);
      return false;
    }
    const observedAtMs = nowMs - observation.ageMs;
    if (previous && (observation.sequence < previous.sequence || frames < previous.frames ||
        nowMs < previous.observedAtMs)) this.invalidateDecoder(nowMs);
    const baseline = this.decoder;
    if (baseline?.sequence === observation.sequence) {
      // A changed counter under the same sample identity is not a new observation.
      if (frames !== baseline.frames) this.invalidateDecoder(nowMs);
      return this.decoderFlat && nowMs - baseline.observedAtMs < FRAME_EVIDENCE_STALE_MS;
    }
    this.decoderFlat = !!baseline && frames === baseline.frames && observedAtMs > baseline.observedAtMs &&
      nowMs - baseline.observedAtMs < FRAME_EVIDENCE_STALE_MS;
    this.decoder = { sequence: observation.sequence, frames, observedAtMs };
    return this.decoderFlat;
  }

  private invalidateDecoder(nowMs: number): void {
    if (this.decoder) { this.lastProgressMs = nowMs; this.clearEpisode(); }
    this.decoder = undefined;
    this.decoderFlat = false;
  }

  private nextAction(elapsed: number): PresentationRecoveryDecision['action'] {
    if (elapsed >= VISIBLE_STALL_REFRESH_MS && !this.refreshRequested) {
      this.refreshRequested = true;
      this.keyframeRequested = true;
      return 'refresh-connection';
    }
    if (!this.keyframeRequested && !this.refreshRequested) {
      this.keyframeRequested = true;
      return 'request-keyframe';
    }
    return 'none';
  }

  private playbackFrames(evidence: FramePresentationEvidence): number | undefined {
    return evidence.playbackTotalFrames !== undefined && evidence.playbackDroppedFrames !== undefined
      ? evidence.playbackTotalFrames - evidence.playbackDroppedFrames : undefined;
  }

  private advanced(current: number | undefined, previous: number | undefined): boolean {
    return current !== undefined && previous !== undefined && current > previous;
  }

  private counterReset(current: FramePresentationEvidence, previous?: FramePresentationEvidence): boolean {
    if (!previous) return false;
    return (['progressSequence', 'presentedFrames', 'decodedFrames', 'playbackTotalFrames', 'playbackDroppedFrames'] as const)
      .some(key => current[key] !== undefined && previous[key] !== undefined && current[key]! < previous[key]!);
  }

  private clearEpisode(): void {
    this.episodeId = undefined;
    this.keyframeRequested = false;
    this.refreshRequested = false;
  }

  private result(state: PresentationRecoveryDecision['state']): PresentationRecoveryDecision {
    return { state, stalledForMs: 0, action: 'none' };
  }
}
